import { Link, useLocation } from 'react-router-dom';
import React, { useEffect, useState } from 'react';

import Button from './Button';
import Icon from '../AppIcon';

const Header = () => {
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isScrolled, setIsScrolled] = useState(false);
	const location = useLocation();

	const navigationItems = [
		{ name: 'Home', path: '/', icon: 'Home' },
		{ name: 'About', path: '/about', icon: 'Users' },
		{ name: 'Attorneys', path: '/attorneys', icon: 'UserCheck' },
		{ name: 'Practice Areas', path: '/practice-areas', icon: 'Scale' },
		{ name: 'Contact', path: '/contact', icon: 'Phone' },
	];

	const moreItems = [
		{ name: 'Publications', path: '/publications', icon: 'FileText' },
		{ name: 'Careers', path: '/careers', icon: 'Briefcase' },
	];

	useEffect(() => {
		const handleScroll = () => {
			setIsScrolled(window.scrollY > 20);
		};

		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	const toggleMenu = () => {
		setIsMenuOpen(!isMenuOpen);
	};

	const closeMenu = () => {
		setIsMenuOpen(false);
	};

	const isActivePath = (path) => {
		return location?.pathname === path;
	};

	return (
		<header
			className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
				isScrolled
					? 'bg-white/95 backdrop-blur-md shadow-premium border-b border-gray-100'
					: 'bg-white/90 backdrop-blur-sm'
			}`}
		>
			<div className='w-full'>
				<div className='flex items-center justify-between h-16 px-4 lg:px-8'>
					{/* Logo */}
					<Link to='/homepage' className='flex items-center group' onClick={closeMenu}>
						<div className='relative'>
							<img
								src='/assets/images/logo.png'
								alt='Fepa Law Partners Logo'
								className='w-48 h-48 object-contain'
								onError={(e) => {
									console.log('Logo failed to load');
									e.target.src = '/assets/images/no_image.png';
								}}
							/>
						</div>
					</Link>

					{/* Desktop Navigation */}
					<nav className='hidden lg:flex items-center space-x-1'>
						{navigationItems?.map((item) => (
							<Link
								key={item?.path}
								to={item?.path}
								className={`relative px-4 py-2 font-source font-bold text-sm transition-all duration-300 uppercase ${
									isActivePath(item?.path)
										? 'text-primary border-b-2 border-primary'
										: 'text-text-primary hover:text-primary hover:border-b-2 hover:border-primary/50'
								}`}
							>
								{item?.name}
							</Link>
						))}

						{/* More Dropdown */}
						<div className='relative group'>
							<button className='flex items-center space-x-2 px-4 py-2 font-source font-bold text-sm text-text-primary hover:text-primary hover:border-b-2 hover:border-primary/50 transition-all duration-300 uppercase'>
								<span>More</span>
								<Icon
									name='ChevronDown'
									size={16}
									className='transition-transform duration-300 group-hover:rotate-180'
								/>
							</button>

							<div className='absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0'>
								<div className='py-2'>
									{moreItems?.map((item) => (
										<Link
											key={item?.path}
											to={item?.path}
											className={`flex items-center space-x-3 px-4 py-2 text-sm font-source font-bold transition-all duration-200 hover:text-primary hover:bg-primary/5 rounded-md mx-1 uppercase ${
												isActivePath(item?.path) ? 'text-primary bg-primary/5' : 'text-text-primary'
											}`}
										>
											<Icon name={item?.icon} size={16} />
											<span>{item?.name}</span>
										</Link>
									))}
								</div>
							</div>
						</div>
					</nav>

					{/* CTA Button & Mobile Menu Toggle */}
					<div className='flex items-center space-x-4'>
						<a
							href='/contact'
							className='hidden sm:flex items-center px-6 py-2 bg-cta hover:bg-cta/90 text-cta-foreground font-source font-semibold rounded-lg shadow-premium premium-shadow-hover transition-all duration-300'
						>
							<Icon name='Calendar' size={16} className='mr-2' />
							Schedule a Consultation
						</a>

						{/* Mobile Menu Toggle */}
						<button
							onClick={toggleMenu}
							className='lg:hidden p-2 rounded-lg text-text-primary hover:text-primary hover:bg-primary/5 transition-colors duration-200'
							aria-label='Toggle menu'
						>
							<Icon
								name={isMenuOpen ? 'X' : 'Menu'}
								size={24}
								className='transition-transform duration-300'
							/>
						</button>
					</div>
				</div>

				{/* Mobile Navigation */}
				<div
					className={`lg:hidden transition-all duration-300 ease-in-out ${
						isMenuOpen
							? 'max-h-96 opacity-100 visible'
							: 'max-h-0 opacity-0 invisible overflow-hidden'
					}`}
				>
					<div className='bg-white/95 backdrop-blur-md border-t border-gray-100'>
						<nav className='px-6 py-6 space-y-3'>
							{[...navigationItems, ...moreItems]?.map((item) => (
								<Link
									key={item?.path}
									to={item?.path}
									onClick={closeMenu}
									className={`flex items-center space-x-4 px-4 py-3 font-source font-bold text-base transition-all duration-200 uppercase ${
										isActivePath(item?.path)
											? 'text-primary border-l-4 border-primary bg-primary/5'
											: 'text-text-primary hover:text-primary hover:border-l-4 hover:border-primary/50 hover:bg-primary/5'
									}`}
								>
									<Icon name={item?.icon} size={18} />
									<span>{item?.name}</span>
								</Link>
							))}

							<div className='pt-4 border-t border-gray-100'>
								<a
									href='/contact'
									className='flex items-center justify-center w-full px-6 py-3 bg-cta hover:bg-cta/90 text-cta-foreground font-source font-semibold rounded-lg shadow-premium transition-all duration-300'
									onClick={closeMenu}
								>
									<Icon name='Calendar' size={16} className='mr-2' />
									Schedule a Consultation
								</a>
							</div>
						</nav>
					</div>
				</div>
			</div>
		</header>
	);
};

export default Header;
